// Curated catalog of well-known REMOTE MCP servers (hosted at a fixed HTTPS URL and documented as
// supporting OAuth or needing no auth, so a tile can start connecting in one click). Per-instance / per-store /
// API-key-only servers (Shopify storefronts, Salesforce, Zendesk, Freshworks, Zoho, etc.) are
// intentionally excluded and reached via "Enter a URL manually". Ordered by relevance to
// external-facing AI agents: customer-facing categories first, developer/infra last. `icon` is a
// "logo:<domain>:<letter>" marker; BrandGlyph renders the brand's real logo from that domain and
// falls back to <letter> if it fails to load.

export const MCP_CATALOG_ICON_COLOR = 'currentColor';

export type McpCatalogCategory =
  | 'Popular'
  | 'Support'
  | 'CRM'
  | 'Scheduling'
  | 'E-commerce'
  | 'Payments'
  | 'Marketing'
  | 'Communication'
  | 'Productivity'
  | 'Data'
  | 'Search'
  | 'Travel'
  | 'Design'
  | 'Developer';

// 'Popular' is a browse-only spotlight rendered from MCP_CATALOG_POPULAR (below); no entry carries
// it as its `category`, so search (which filters the flat catalog) never double-lists these servers.
export const MCP_CATALOG_CATEGORY_ORDER: McpCatalogCategory[] = [
  'Popular',
  'Productivity',
  'CRM',
  'Support',
  'Payments',
  'Marketing',
  'Communication',
  'E-commerce',
  'Scheduling',
  'Developer',
  'Data',
  'Search',
  'Design',
  'Travel',
];

export type McpCatalogEntry = {
  name: string;
  url: string;
  icon: string;
  category: McpCatalogCategory;
  description: string;
  descriptionKey?: string;
  // Mirrors curated backend profiles.
  isTuned?: boolean;
};

export const MCP_CATALOG: McpCatalogEntry[] = [
  // Productivity
  {
    name: 'Notion',
    url: 'https://mcp.notion.com/mcp',
    icon: 'logo:notion.so:N',
    category: 'Productivity',
    description: 'Read and write your workspace',
    isTuned: true,
  },
  {
    name: 'Linear',
    url: 'https://mcp.linear.app/mcp',
    icon: 'logo:linear.app:L',
    category: 'Productivity',
    description: 'Create, search, and update issues',
  },
  {
    name: 'monday',
    url: 'https://mcp.monday.com/mcp',
    icon: 'logo:monday.com:M',
    category: 'Productivity',
    description: 'Manage boards and items',
  },
  {
    name: 'Airtable',
    url: 'https://mcp.airtable.com/mcp',
    icon: 'logo:airtable.com:A',
    category: 'Productivity',
    description: 'Query and edit your bases',
  },
  {
    name: 'Trello',
    url: 'https://mcp.trello.com/v1',
    icon: 'logo:trello.com:T',
    category: 'Productivity',
    description: 'Manage boards, cards, lists',
  },
  {
    name: 'Todoist',
    url: 'https://ai.todoist.net/mcp',
    icon: 'logo:todoist.com:T',
    category: 'Productivity',
    description: 'Create and manage tasks',
  },
  {
    name: 'Miro',
    url: 'https://mcp.miro.com/',
    icon: 'logo:miro.com:M',
    category: 'Productivity',
    description: 'Create and edit board content',
  },
  {
    name: 'WordPress.com',
    url: 'https://public-api.wordpress.com/wpcom/v2/mcp/v1',
    icon: 'logo:wordpress.com:W',
    category: 'Productivity',
    description: 'Manage posts, media, sites',
  },
  {
    name: 'DeepL',
    url: 'https://mcp.deepl.com/v1/mcp',
    icon: 'logo:deepl.com:D',
    category: 'Productivity',
    description: 'Translate text between languages',
  },
  {
    name: 'Coda',
    url: 'https://coda.io/apis/mcp',
    icon: 'logo:coda.io:C',
    category: 'Productivity',
    description: 'Read and edit Coda docs',
  },
  {
    name: 'Fireflies',
    url: 'https://api.fireflies.ai/mcp',
    icon: 'logo:fireflies.ai:F',
    category: 'Productivity',
    description: 'Search meeting transcripts',
  },
  {
    name: 'Otter.ai',
    url: 'https://mcp.otter.ai/',
    icon: 'logo:otter.ai:O',
    category: 'Productivity',
    description: 'Search meeting transcripts and notes',
  },
  {
    name: 'PandaDoc',
    url: 'https://mcp.pandadoc.com/v1/mcp',
    icon: 'logo:pandadoc.com:P',
    category: 'Productivity',
    description: 'Create and send documents',
  },
  {
    name: 'SignNow',
    url: 'https://mcp-server.signnow.com/mcp',
    icon: 'logo:signnow.com:S',
    category: 'Productivity',
    description: 'Send documents for e-signature',
  },
  {
    name: 'Gusto',
    url: 'https://mcp.api.gusto.com/',
    icon: 'logo:gusto.com:G',
    category: 'Productivity',
    description: 'Manage payroll and HR',
  },
  {
    name: 'Sanity',
    url: 'https://mcp.sanity.io/mcp',
    icon: 'logo:sanity.io:S',
    category: 'Productivity',
    description: 'Query, edit, publish content',
  },
  {
    name: 'Contentful',
    url: 'https://mcp.contentful.com/mcp',
    icon: 'logo:contentful.com:C',
    category: 'Productivity',
    description: 'Manage content entries',
  },
  {
    name: 'GitBook',
    url: 'https://mcp.gitbook.com/mcp',
    icon: 'logo:gitbook.com:G',
    category: 'Productivity',
    description: 'Search and edit documentation',
  },
  {
    name: 'Fibery',
    url: 'https://mcp.fibery.io/mcp',
    icon: 'logo:fibery.io:F',
    category: 'Productivity',
    description: 'Query and update workspace',
  },
  {
    name: 'Grain',
    url: 'https://api.grain.com/_/mcp',
    icon: 'logo:grain.com:G',
    category: 'Productivity',
    description: 'Search meeting notes and deals',
  },
  {
    name: 'Egnyte',
    url: 'https://mcp-server.egnyte.com/sse',
    icon: 'logo:egnyte.com:E',
    category: 'Productivity',
    description: 'Search and manage files',
  },
  {
    name: 'Ashby',
    url: 'https://mcp.ashbyhq.com/mcp/v1',
    icon: 'logo:ashbyhq.com:A',
    category: 'Productivity',
    description: 'Manage recruiting pipelines',
  },
  {
    name: 'Avoma',
    url: 'https://mcp.avoma.com/mcp',
    icon: 'logo:avoma.com:A',
    category: 'Productivity',
    description: 'Analyze meeting recordings and notes',
  },
  {
    name: 'Circleback',
    url: 'https://circleback.ai/api/mcp',
    icon: 'logo:circleback.ai:C',
    category: 'Productivity',
    description: 'Get meeting notes and action items',
  },
  {
    name: 'Crowdin',
    url: 'https://mcp.crowdin.com/',
    icon: 'logo:crowdin.com:C',
    category: 'Productivity',
    description: 'Manage localization projects',
  },
  {
    name: 'Fellow',
    url: 'https://fellow.app/mcp',
    icon: 'logo:fellow.app:F',
    category: 'Productivity',
    description: 'Manage meeting agendas and notes',
  },
  {
    name: 'Granola',
    url: 'https://mcp.granola.ai/mcp',
    icon: 'logo:granola.ai:G',
    category: 'Productivity',
    description: 'Search meeting notes and actions',
  },
  {
    name: 'Jotform',
    url: 'https://mcp.jotform.com',
    icon: 'logo:jotform.com:J',
    category: 'Productivity',
    description: 'Manage forms and submissions',
  },
  {
    name: 'Kahoot!',
    url: 'https://mcp.kahoot.it/mcp',
    icon: 'logo:kahoot.com:K',
    category: 'Productivity',
    description: 'Create quizzes and learning games',
  },
  {
    name: 'Krisp',
    url: 'https://mcp.krisp.ai',
    icon: 'logo:krisp.ai:K',
    category: 'Productivity',
    description: 'Access meeting notes and transcripts',
  },
  {
    name: 'Lingo.dev',
    url: 'https://mcp.lingo.dev',
    icon: 'logo:lingo.dev:L',
    category: 'Productivity',
    description: 'Automate AI localization',
  },
  {
    name: 'Lokalise',
    url: 'https://mcp.lokalise.com',
    icon: 'logo:lokalise.com:L',
    category: 'Productivity',
    description: 'Manage localization and translations',
  },
  {
    name: 'Navan',
    url: 'https://mcp.navan.com/mcp',
    icon: 'logo:navan.com:N',
    category: 'Productivity',
    description: 'Book travel and manage expenses',
  },
  {
    name: 'POEditor',
    url: 'https://mcp.poeditor.com/mcp',
    icon: 'logo:poeditor.com:P',
    category: 'Productivity',
    description: 'Manage translation strings',
  },
  {
    name: 'Sunsama',
    url: 'https://api.sunsama.com/mcp',
    icon: 'logo:sunsama.com:S',
    category: 'Productivity',
    description: 'Plan daily tasks and calendar',
  },
  {
    name: 'Voicenotes',
    url: 'https://api.voicenotes.com',
    icon: 'logo:voicenotes.com:V',
    category: 'Productivity',
    description: 'Search voice notes and ideas',
  },
  {
    name: 'Weglot',
    url: 'https://mcp.weglot.com',
    icon: 'logo:weglot.com:W',
    category: 'Productivity',
    description: 'Translate website content',
  },
  {
    name: 'Workable',
    url: 'https://mcp.workable.com/mcp',
    icon: 'logo:workable.com:W',
    category: 'Productivity',
    description: 'Manage hiring and candidates',
  },
  {
    name: 'Avaza',
    url: 'https://mcp.avaza.com/mcp',
    icon: 'logo:avaza.com:A',
    category: 'Productivity',
    description: 'Manage projects and invoicing',
  },
  {
    name: 'Certifier',
    url: 'https://api.certifier.io/v1/mcp',
    icon: 'logo:certifier.io:C',
    category: 'Productivity',
    description: 'Issue certificates and badges',
  },
  {
    name: 'Metaview',
    url: 'https://mcp.metaview.ai/mcp',
    icon: 'logo:metaview.ai:M',
    category: 'Productivity',
    description: 'Get recruiting interview notes',
  },
  {
    name: 'MotaWord',
    url: 'https://api.motaword.com',
    icon: 'logo:motaword.com:M',
    category: 'Productivity',
    description: 'Order human translations',
  },
  {
    name: 'Tallyfy',
    url: 'https://mcp.tallyfy.com',
    icon: 'logo:tallyfy.com:T',
    category: 'Productivity',
    description: 'Run workflow processes',
  },
  {
    name: 'ClickUp',
    url: 'https://mcp.clickup.com/mcp',
    icon: 'logo:clickup.com:C',
    category: 'Productivity',
    description: 'Manage tasks, docs, and goals',
    descriptionKey: 'catalog.descriptions.clickup',
  },
  // CRM
  {
    name: 'Attio',
    url: 'https://mcp.attio.com/mcp',
    icon: 'logo:attio.com:A',
    category: 'CRM',
    description: 'Manage CRM records and notes',
  },
  {
    name: 'Pipedrive',
    url: 'https://mcp.pipedrive.ai/mcp',
    icon: 'logo:pipedrive.com:P',
    category: 'CRM',
    description: 'Manage deals and contacts',
  },
  {
    name: 'Apollo',
    url: 'https://mcp.apollo.io/mcp',
    icon: 'logo:apollo.io:A',
    category: 'CRM',
    description: 'Find and enrich prospects',
  },
  {
    name: 'Clay',
    url: 'https://api.clay.com/v3/mcp',
    icon: 'logo:clay.com:C',
    category: 'CRM',
    description: 'Prospect and enrich leads',
  },
  {
    name: 'Outreach',
    url: 'https://api.outreach.io/mcp',
    icon: 'logo:outreach.io:O',
    category: 'CRM',
    description: 'Manage prospects and sequences',
  },
  {
    name: 'folk',
    url: 'https://mcp.folk.app/mcp',
    icon: 'logo:folk.app:F',
    category: 'CRM',
    description: 'Manage contacts, companies, deals',
  },
  {
    name: 'Salesloft',
    url: 'https://mcp.salesloft.com/mcp',
    icon: 'logo:salesloft.com:S',
    category: 'CRM',
    description: 'Run sales cadences, log activity',
  },
  {
    name: 'Clarify',
    url: 'https://api.clarify.ai/mcp',
    icon: 'logo:clarify.ai:C',
    category: 'CRM',
    description: 'Query CRM and create records',
  },
  {
    name: 'Lightfield',
    url: 'https://mcp.lightfield.app/mcp',
    icon: 'logo:lightfield.app:L',
    category: 'CRM',
    description: 'Operate an AI-native CRM',
  },
  {
    name: 'Twenty',
    url: 'https://api.twenty.com/mcp',
    icon: 'logo:twenty.com:T',
    category: 'CRM',
    description: 'Query and create CRM records',
  },
  {
    name: 'Bitrix24',
    url: 'https://mcp.bitrix24.com/mcp/',
    icon: 'logo:bitrix24.com:B',
    category: 'CRM',
    description: 'Manage leads, deals, contacts',
  },
  {
    name: 'Streak',
    url: 'https://api.streak.com/mcp',
    icon: 'logo:streak.com:S',
    category: 'CRM',
    description: 'Manage pipelines and contacts',
  },
  {
    name: 'lemlist',
    url: 'https://app.lemlist.com/mcp',
    icon: 'logo:lemlist.com:L',
    category: 'CRM',
    description: 'Create leads, run outreach',
  },
  {
    name: 'Amplemarket',
    url: 'https://mcp.amplemarket.com/mcp',
    icon: 'logo:amplemarket.com:A',
    category: 'CRM',
    description: 'Find and enrich leads',
  },
  {
    name: 'Day.ai',
    url: 'https://day.ai/api/mcp',
    icon: 'logo:day.ai:D',
    category: 'CRM',
    description: 'Query CRM customer context',
  },
  {
    name: 'Common Room',
    url: 'https://mcp.commonroom.io/mcp',
    icon: 'logo:commonroom.io:C',
    category: 'CRM',
    description: 'Query GTM signals and contacts',
  },
  {
    name: 'Hunter',
    url: 'https://mcp.hunter.io/mcp',
    icon: 'logo:hunter.io:H',
    category: 'CRM',
    description: 'Find and verify email addresses',
  },
  {
    name: 'Breakcold',
    url: 'https://http.us.breakcold.com/mcp/v1',
    icon: 'logo:breakcold.com:B',
    category: 'CRM',
    description: 'Manage social-selling CRM',
  },
  {
    name: 'Close',
    url: 'https://mcp.close.com/',
    icon: 'logo:close.com:C',
    category: 'CRM',
    description: 'Manage leads and sales pipeline',
  },
  {
    name: 'Crossbeam',
    url: 'https://mcp.crossbeam.com',
    icon: 'logo:crossbeam.com:C',
    category: 'CRM',
    description: 'Map partner account overlaps',
  },
  // Support
  {
    name: 'Gorgias',
    url: 'https://mcp.gorgias.com/mcp',
    icon: 'logo:gorgias.com:G',
    category: 'Support',
    description: 'Manage ecommerce support tickets',
  },
  {
    name: 'Help Scout',
    url: 'https://mcp-beta.helpscout.net/mcp',
    icon: 'logo:helpscout.com:H',
    category: 'Support',
    description: 'Search conversations and customers',
  },
  {
    name: 'Pylon',
    url: 'https://mcp.usepylon.com/',
    icon: 'logo:usepylon.com:P',
    category: 'Support',
    description: 'Manage B2B support tickets',
  },
  {
    name: 'Plain',
    url: 'https://mcp.plain.com/mcp',
    icon: 'logo:plain.com:P',
    category: 'Support',
    description: 'Manage support tickets and threads',
  },
  {
    name: 'Unthread',
    url: 'https://app.unthread.io/api/mcp',
    icon: 'logo:unthread.io:U',
    category: 'Support',
    description: 'Manage Slack support tickets',
  },
  {
    name: 'Siena',
    url: 'https://mcp.siena.cx/mcp',
    icon: 'logo:siena.cx:S',
    category: 'Support',
    description: 'Run autonomous CX actions',
  },
  {
    name: 'Muninx',
    url: 'https://api.muninx.com/mcp',
    icon: 'logo:muninx.com:M',
    category: 'Support',
    description: 'Manage helpdesk tickets',
  },
  {
    name: 'Unwrap',
    url: 'https://nlp.api.production.unwrap.ai/mcp',
    icon: 'logo:unwrap.ai:U',
    category: 'Support',
    description: 'Analyze customer feedback',
  },
  // Payments
  {
    name: 'Stripe',
    url: 'https://mcp.stripe.com',
    icon: 'logo:stripe.com:S',
    category: 'Payments',
    description: 'Look up payments and refunds',
  },
  {
    name: 'PayPal',
    url: 'https://mcp.paypal.com/mcp',
    icon: 'logo:paypal.com:P',
    category: 'Payments',
    description: 'Create invoices and track orders',
  },
  {
    name: 'Brex',
    url: 'https://api.brex.com/mcp',
    icon: 'logo:brex.com:B',
    category: 'Payments',
    description: 'Manage cards, expenses, budgets',
  },
  {
    name: 'Ramp',
    url: 'https://mcp.ramp.com/mcp',
    icon: 'logo:ramp.com:R',
    category: 'Payments',
    description: 'Manage cards, bills, spend',
  },
  {
    name: 'Mercury',
    url: 'https://mcp.mercury.com/mcp',
    icon: 'logo:mercury.com:M',
    category: 'Payments',
    description: 'Read business bank data',
  },
  {
    name: 'Avalara',
    url: 'https://mcp.avalara.com/avatax',
    icon: 'logo:avalara.com:A',
    category: 'Payments',
    description: 'Calculate tax compliance',
  },
  {
    name: 'GoCardless',
    url: 'https://mcp.gocardless.com',
    icon: 'logo:gocardless.com:G',
    category: 'Payments',
    description: 'Look up payments and payouts',
  },
  {
    name: 'Airwallex',
    url: 'https://mcp.airwallex.com/mcp',
    icon: 'logo:airwallex.com:A',
    category: 'Payments',
    description: 'Move money, cards, accounts',
  },
  {
    name: 'Dodo Payments',
    url: 'https://mcp.dodopayments.com/mcp',
    icon: 'logo:dodopayments.com:D',
    category: 'Payments',
    description: 'Manage billing and subscriptions',
  },
  {
    name: 'Recurly',
    url: 'https://mcp.recurly.com/mcp',
    icon: 'logo:recurly.com:R',
    category: 'Payments',
    description: 'Manage subscription billing',
  },
  {
    name: 'Cashfree',
    url: 'https://mcp.cashfree.com',
    icon: 'logo:cashfree.com:C',
    category: 'Payments',
    description: 'Process payments and payouts',
  },
  {
    name: 'Hyperline',
    url: 'https://mcp.hyperline.co/mcp',
    icon: 'logo:hyperline.co:H',
    category: 'Payments',
    description: 'Manage billing and subscriptions',
  },
  {
    name: 'Numeric',
    url: 'https://api.numeric.io/mcp',
    icon: 'logo:numeric.io:N',
    category: 'Payments',
    description: 'Automate the accounting close',
  },
  // Marketing
  {
    name: 'Klaviyo',
    url: 'https://mcp.klaviyo.com/mcp',
    icon: 'logo:klaviyo.com:K',
    category: 'Marketing',
    description: 'Manage lists, segments, campaigns',
  },
  {
    name: 'Resend',
    url: 'https://mcp.resend.com/mcp',
    icon: 'logo:resend.com:R',
    category: 'Marketing',
    description: 'Send emails and manage contacts',
  },
  {
    name: 'Brevo',
    url: 'https://mcp.brevo.com/v1/brevo/mcp',
    icon: 'logo:brevo.com:B',
    category: 'Marketing',
    description: 'Manage contacts and campaigns',
  },
  {
    name: 'Customer.io',
    url: 'https://mcp.customer.io/mcp',
    icon: 'logo:customer.io:C',
    category: 'Marketing',
    description: 'Manage journeys and campaigns',
  },
  {
    name: 'MailerLite',
    url: 'https://mcp.mailerlite.com/mcp',
    icon: 'logo:mailerlite.com:M',
    category: 'Marketing',
    description: 'Manage subscribers and campaigns',
  },
  {
    name: 'MailerSend',
    url: 'https://mcp.mailersend.com/mcp',
    icon: 'logo:mailersend.com:M',
    category: 'Marketing',
    description: 'Send email and manage templates',
  },
  {
    name: 'Kit',
    url: 'https://app.kit.com/mcp',
    icon: 'logo:kit.com:K',
    category: 'Marketing',
    description: 'Manage subscribers and broadcasts',
  },
  {
    name: 'beehiiv',
    url: 'https://mcp.beehiiv.com/mcp',
    icon: 'logo:beehiiv.com:B',
    category: 'Marketing',
    description: 'Read subscribers, posts, metrics',
  },
  {
    name: 'AgentMail',
    url: 'https://mcp.agentmail.to/mcp',
    icon: 'logo:agentmail.to:A',
    category: 'Marketing',
    description: 'Create inboxes and send email',
  },
  {
    name: 'Transmit',
    url: 'https://mcp.xmit.sh/mcp',
    icon: 'logo:xmit.sh:T',
    category: 'Marketing',
    description: 'Send transactional email',
  },
  {
    name: 'Brandfetch',
    url: 'https://mcp.brandfetch.io/mcp',
    icon: 'logo:brandfetch.com:B',
    category: 'Design',
    description: 'Fetch brand logos and assets',
  },
  {
    name: 'EZTexting',
    url: 'https://mcp.eztexting.com/mcp',
    icon: 'logo:eztexting.com:E',
    category: 'Marketing',
    description: 'Send SMS marketing campaigns',
  },
  {
    name: 'Infobip',
    url: 'https://mcp.infobip.com/sms',
    icon: 'logo:infobip.com:I',
    category: 'Communication',
    description: 'Send SMS and omnichannel messages',
  },
  // Communication
  {
    name: 'Knock',
    url: 'https://mcp.knock.app/mcp',
    icon: 'logo:knock.app:K',
    category: 'Communication',
    description: 'Trigger notification workflows',
  },
  {
    name: 'Novu',
    url: 'https://mcp.novu.co/',
    icon: 'logo:novu.co:N',
    category: 'Communication',
    description: 'Trigger notifications to users',
  },
  {
    name: 'Bird',
    url: 'https://mcp.platform.bird.com/mcp',
    icon: 'logo:bird.com:B',
    category: 'Communication',
    description: 'Send SMS, email, WhatsApp',
  },
  {
    name: 'Synthflow',
    url: 'https://mcp.synthflow.ai/mcp',
    icon: 'logo:synthflow.ai:S',
    category: 'Communication',
    description: 'Manage AI voice agents',
  },
  {
    name: 'Cartesia',
    url: 'https://mcp.cartesia.ai/mcp',
    icon: 'logo:cartesia.ai:C',
    category: 'Communication',
    description: 'Generate and clone voices',
  },
  // E-commerce
  {
    name: 'Wix',
    url: 'https://mcp.wix.com/mcp',
    icon: 'logo:wix.com:W',
    category: 'E-commerce',
    description: 'Manage Wix sites and stores',
  },
  {
    name: 'AfterShip',
    url: 'https://mcp.aftership.com/tracking/public',
    icon: 'logo:aftership.com:A',
    category: 'E-commerce',
    description: 'Track shipments and returns',
  },
  {
    name: 'Saleor',
    url: 'https://mcp.saleor.app/mcp',
    icon: 'logo:saleor.io:S',
    category: 'E-commerce',
    description: 'Manage commerce orders and products',
  },
  {
    name: 'Fourthwall',
    url: 'https://mcp.fourthwall.com',
    icon: 'logo:fourthwall.com:F',
    category: 'E-commerce',
    description: 'Manage orders, products, refunds',
  },
  {
    name: 'Commerce Layer',
    url: 'https://core-mcp.commercelayer.io/mcp',
    icon: 'logo:commercelayer.io:C',
    category: 'E-commerce',
    description: 'Manage commerce orders and SKUs',
  },
  {
    name: 'DSers',
    url: 'https://mcp.dsers.com/dropshipping/mcp',
    icon: 'logo:dsers.com:D',
    category: 'E-commerce',
    description: 'Manage dropshipping orders',
  },
  {
    name: 'Loop Returns',
    url: 'https://api.loopreturns.com/mcp',
    icon: 'logo:loopreturns.com:L',
    category: 'E-commerce',
    description: 'Manage ecommerce returns',
  },
  {
    name: 'parcelLab',
    url: 'https://agents.parcellab.com/internal/mcp/',
    icon: 'logo:parcellab.com:P',
    category: 'E-commerce',
    description: 'Track post-purchase delivery',
  },
  // Scheduling
  {
    name: 'Calendly',
    url: 'https://mcp.calendly.com',
    icon: 'logo:calendly.com:C',
    category: 'Scheduling',
    description: 'Schedule and manage meetings',
    isTuned: true,
  },
  {
    name: 'Cal.com',
    url: 'https://mcp.cal.com/mcp',
    icon: 'logo:cal.com:C',
    category: 'Scheduling',
    description: 'Book, reschedule, and cancel meetings',
  },
  {
    name: 'Cronofy',
    url: 'https://api.cronofy.com/v1/mcp_server',
    icon: 'logo:cronofy.com:C',
    category: 'Scheduling',
    description: 'Check availability and book meetings',
  },
  // Developer
  {
    name: 'Atlassian',
    url: 'https://mcp.atlassian.com/v1/mcp/authv2',
    icon: 'logo:atlassian.com:A',
    category: 'Developer',
    description: 'Search Jira, Confluence, and Bitbucket',
  },
  {
    name: 'Sentry',
    url: 'https://mcp.sentry.dev/mcp',
    icon: 'logo:sentry.io:S',
    category: 'Developer',
    description: 'Triage and debug errors',
  },
  {
    name: 'Cloudflare',
    url: 'https://mcp.cloudflare.com/mcp',
    icon: 'logo:cloudflare.com:C',
    category: 'Developer',
    description: 'Manage Cloudflare infrastructure',
  },
  {
    name: 'GitLab',
    url: 'https://gitlab.com/api/v4/mcp',
    icon: 'logo:gitlab.com:G',
    category: 'Developer',
    description: 'Manage repos, issues, and MRs',
  },
  {
    name: 'Datadog',
    url: 'https://mcp.datadoghq.com/api/unstable/mcp-server/mcp',
    icon: 'logo:datadoghq.com:D',
    category: 'Developer',
    description: 'Query metrics, logs, and traces',
  },
  {
    name: 'Replicate',
    url: 'https://mcp.replicate.com/mcp',
    icon: 'logo:replicate.com:R',
    category: 'Developer',
    description: 'Run and search AI models',
  },
  {
    name: 'Netlify',
    url: 'https://netlify-mcp.netlify.app/mcp',
    icon: 'logo:netlify.com:N',
    category: 'Developer',
    description: 'Create, deploy, and manage sites',
  },
  {
    name: 'Heroku',
    url: 'https://mcp.heroku.com/mcp',
    icon: 'logo:heroku.com:H',
    category: 'Developer',
    description: 'Deploy and manage apps',
  },
  {
    name: 'Postman',
    url: 'https://mcp.postman.com/mcp',
    icon: 'logo:postman.com:P',
    category: 'Developer',
    description: 'Run collections and manage APIs',
  },
  {
    name: 'Grafana',
    url: 'https://mcp.grafana.com/mcp',
    icon: 'logo:grafana.com:G',
    category: 'Developer',
    description: 'Query dashboards and alerts',
  },
  {
    name: 'New Relic',
    url: 'https://mcp.newrelic.com/mcp',
    icon: 'logo:newrelic.com:N',
    category: 'Developer',
    description: 'Query telemetry and alerts',
  },
  {
    name: 'Firecrawl',
    url: 'https://mcp.firecrawl.dev/v2/mcp',
    icon: 'logo:firecrawl.dev:F',
    category: 'Developer',
    description: 'Scrape and crawl websites',
  },
  {
    name: 'Mem0',
    url: 'https://mcp.mem0.ai/',
    icon: 'logo:mem0.ai:M',
    category: 'Developer',
    description: 'Store and recall AI memory',
  },
  {
    name: 'Vanta',
    url: 'https://mcp.vanta.com',
    icon: 'logo:vanta.com:V',
    category: 'Developer',
    description: 'Track security compliance status',
  },
  {
    name: 'WorkOS',
    url: 'https://mcp.workos.com/mcp',
    icon: 'logo:workos.com:W',
    category: 'Developer',
    description: 'Manage enterprise SSO and users',
  },
  {
    name: 'Sourcegraph',
    url: 'https://sourcegraph.com/.api/mcp/v1',
    icon: 'logo:sourcegraph.com:S',
    category: 'Developer',
    description: 'Search and navigate code',
  },
  {
    name: 'Railway',
    url: 'https://mcp.railway.com',
    icon: 'logo:railway.com:R',
    category: 'Developer',
    description: 'Deploy and manage projects',
  },
  {
    name: 'Buildkite',
    url: 'https://mcp.buildkite.com/mcp',
    icon: 'logo:buildkite.com:B',
    category: 'Developer',
    description: 'Query CI/CD builds and logs',
  },
  {
    name: 'Semgrep',
    url: 'https://mcp.semgrep.ai/mcp',
    icon: 'logo:semgrep.dev:S',
    category: 'Developer',
    description: 'Scan code for vulnerabilities',
  },
  {
    name: 'Globalping',
    url: 'https://mcp.globalping.dev/mcp',
    icon: 'logo:globalping.io:G',
    category: 'Developer',
    description: 'Run network tests globally',
  },
  {
    name: 'DigitalOcean',
    url: 'https://apps.mcp.digitalocean.com/mcp',
    icon: 'logo:digitalocean.com:D',
    category: 'Developer',
    description: 'Manage apps and infrastructure',
  },
  {
    name: 'Pulumi',
    url: 'https://mcp.ai.pulumi.com/mcp',
    icon: 'logo:pulumi.com:P',
    category: 'Developer',
    description: 'Query and manage stacks',
  },
  {
    name: 'LaunchDarkly',
    url: 'https://mcp.launchdarkly.com/mcp/launchdarkly',
    icon: 'logo:launchdarkly.com:L',
    category: 'Developer',
    description: 'Manage flags and experiments',
  },
  {
    name: 'Honeycomb',
    url: 'https://mcp.honeycomb.io/mcp',
    icon: 'logo:honeycomb.io:H',
    category: 'Developer',
    description: 'Query traces and dashboards',
  },
  {
    name: 'Axiom',
    url: 'https://mcp.axiom.co/mcp',
    icon: 'logo:axiom.co:A',
    category: 'Developer',
    description: 'Query logs and traces',
  },
  {
    name: 'incident.io',
    url: 'https://mcp.incident.io/mcp',
    icon: 'logo:incident.io:I',
    category: 'Developer',
    description: 'Manage incidents and on-call',
  },
  {
    name: 'Microsoft Learn',
    url: 'https://learn.microsoft.com/api/mcp',
    icon: 'logo:microsoft.com:M',
    category: 'Developer',
    description: 'Search Microsoft docs',
  },
  {
    name: 'AWS Knowledge',
    url: 'https://knowledge-mcp.global.api.aws/mcp',
    icon: 'logo:aws.amazon.com:A',
    category: 'Developer',
    description: 'Search AWS docs',
  },
  {
    name: 'Better Stack',
    url: 'https://mcp.betterstack.com',
    icon: 'logo:betterstack.com:B',
    category: 'Developer',
    description: 'Monitor uptime and incidents',
  },
  {
    name: 'Braintrust',
    url: 'https://api.braintrust.dev',
    icon: 'logo:braintrust.dev:B',
    category: 'Developer',
    description: 'Run and inspect LLM evals',
  },
  {
    name: 'BugSnag',
    url: 'https://bugsnag.mcp.smartbear.com/mcp',
    icon: 'logo:bugsnag.com:B',
    category: 'Developer',
    description: 'Investigate app errors and crashes',
  },
  {
    name: 'Builder.io',
    url: 'https://mcp.builder.io/mcp/publish',
    icon: 'logo:builder.io:B',
    category: 'Developer',
    description: 'Publish visual CMS content',
  },
  {
    name: 'CData',
    url: 'https://mcp.cloud.cdata.com/mcp',
    icon: 'logo:cdata.com:C',
    category: 'Developer',
    description: 'Connect to enterprise data sources',
  },
  {
    name: 'InstantDB',
    url: 'https://mcp.instantdb.com/',
    icon: 'logo:instantdb.com:I',
    category: 'Developer',
    description: 'Query the realtime database',
  },
  {
    name: 'Jam',
    url: 'https://mcp.jam.dev/mcp',
    icon: 'logo:jam.dev:J',
    category: 'Developer',
    description: 'Create and triage bug reports',
  },
  {
    name: 'Magic Patterns',
    url: 'https://mcp.magicpatterns.com',
    icon: 'logo:magicpatterns.com:M',
    category: 'Developer',
    description: 'Generate UI from prompts',
  },
  {
    name: 'NocoDB',
    url: 'https://app.nocodb.com/mcp',
    icon: 'logo:nocodb.com:N',
    category: 'Developer',
    description: 'Manage no-code database tables',
  },
  {
    name: 'Octoparse',
    url: 'https://mcp.octoparse.com/',
    icon: 'logo:octoparse.com:O',
    category: 'Developer',
    description: 'Extract web data with no code',
  },
  {
    name: 'OVHcloud',
    url: 'https://mcp.eu.ovhcloud.com',
    icon: 'logo:ovhcloud.com:O',
    category: 'Developer',
    description: 'Manage cloud servers and services',
  },
  {
    name: 'Polar Signals',
    url: 'https://api.polarsignals.com/',
    icon: 'logo:polarsignals.com:P',
    category: 'Developer',
    description: 'Query continuous profiling data',
  },
  {
    name: 'Roboflow',
    url: 'https://mcp.roboflow.com/mcp',
    icon: 'logo:roboflow.com:R',
    category: 'Developer',
    description: 'Build computer vision models',
  },
  {
    name: 'Scrapfly',
    url: 'https://mcp.scrapfly.io',
    icon: 'logo:scrapfly.io:S',
    category: 'Developer',
    description: 'Scrape web pages at scale',
  },
  {
    name: 'Shortcut',
    url: 'https://mcp.shortcut.com/mcp',
    icon: 'logo:shortcut.com:S',
    category: 'Developer',
    description: 'Manage stories and sprints',
  },
  {
    name: 'Statsig',
    url: 'https://api.statsig.com/v1/mcp',
    icon: 'logo:statsig.com:S',
    category: 'Developer',
    description: 'Manage feature flags and experiments',
  },
  {
    name: 'Stytch',
    url: 'https://mcp.stytch.dev',
    icon: 'logo:stytch.com:S',
    category: 'Developer',
    description: 'Manage authentication and users',
  },
  {
    name: 'Wiz',
    url: 'https://mcp.app.wiz.io/',
    icon: 'logo:wiz.io:W',
    category: 'Developer',
    description: 'Query cloud security findings',
  },
  {
    name: 'Alchemy',
    url: 'https://mcp.alchemy.com/mcp',
    icon: 'logo:alchemy.com:A',
    category: 'Developer',
    description: 'Query blockchain and web3 data',
  },
  {
    name: 'Arcjet',
    url: 'https://api.arcjet.com/mcp',
    icon: 'logo:arcjet.com:A',
    category: 'Developer',
    description: 'Protect apps from abuse',
  },
  {
    name: 'Coralogix',
    url: 'https://api.us2.coralogix.com/mgmt/api/v1/mcp',
    icon: 'logo:coralogix.com:C',
    category: 'Developer',
    description: 'Query logs and observability',
  },
  {
    name: 'Cortex',
    url: 'https://mcp.cortex.io/mcp',
    icon: 'logo:cortex.io:C',
    category: 'Developer',
    description: 'Query the service catalog',
  },
  {
    name: 'Cypress',
    url: 'https://mcp.cypress.io/mcp',
    icon: 'logo:cypress.io:C',
    category: 'Developer',
    description: 'Run and debug E2E tests',
  },
  {
    name: 'Descope',
    url: 'https://mcp.descope.com',
    icon: 'logo:descope.com:D',
    category: 'Developer',
    description: 'Manage authentication flows',
  },
  {
    name: 'Drata',
    url: 'https://mcp.drata.com',
    icon: 'logo:drata.com:D',
    category: 'Developer',
    description: 'Track security compliance',
  },
  {
    name: 'Equinix',
    url: 'https://mcp.equinix.com/fabric',
    icon: 'logo:equinix.com:E',
    category: 'Developer',
    description: 'Manage Fabric interconnections',
  },
  {
    name: 'Eraser',
    url: 'https://app.eraser.io/api/mcp',
    icon: 'logo:eraser.io:E',
    category: 'Developer',
    description: 'Create diagrams and docs',
  },
  {
    name: 'Greptile',
    url: 'https://api.greptile.com/mcp',
    icon: 'logo:greptile.com:G',
    category: 'Developer',
    description: 'Search and understand codebases',
  },
  {
    name: 'GTmetrix',
    url: 'https://gtmetrix.com/mcp',
    icon: 'logo:gtmetrix.com:G',
    category: 'Developer',
    description: 'Test website performance',
  },
  {
    name: 'mabl',
    url: 'https://mcp.mabl.com/',
    icon: 'logo:mabl.com:M',
    category: 'Developer',
    description: 'Run low-code test automation',
  },
  {
    name: 'Mermaid Chart',
    url: 'https://mcp.mermaidchart.com/mcp',
    icon: 'logo:mermaidchart.com:M',
    category: 'Developer',
    description: 'Create and render diagrams',
  },
  {
    name: 'Monte Carlo',
    url: 'https://mcp.getmontecarlo.com/mcp',
    icon: 'logo:montecarlodata.com:M',
    category: 'Developer',
    description: 'Monitor data quality',
  },
  {
    name: 'Prowler',
    url: 'https://mcp.prowler.com/mcp',
    icon: 'logo:prowler.com:P',
    category: 'Developer',
    description: 'Review cloud security checks',
  },
  {
    name: 'Rootly',
    url: 'https://mcp.rootly.com',
    icon: 'logo:rootly.com:R',
    category: 'Developer',
    description: 'Manage incidents and on-call',
  },
  {
    name: 'Socket',
    url: 'https://mcp.socket.dev/',
    icon: 'logo:socket.dev:S',
    category: 'Developer',
    description: 'Scan dependencies for threats',
  },
  {
    name: 'Tigris',
    url: 'https://mcp.storage.dev/mcp',
    icon: 'logo:tigrisdata.com:T',
    category: 'Developer',
    description: 'Manage object storage buckets',
  },
  {
    name: 'Supermemory',
    url: 'https://mcp.supermemory.ai/mcp',
    icon: 'logo:supermemory.ai:S',
    category: 'Developer',
    description: 'Store and search AI memory',
  },
  {
    name: 'Lovable',
    url: 'https://mcp.lovable.dev',
    icon: 'logo:lovable.dev:L',
    category: 'Developer',
    description: 'Build and edit Lovable projects',
    descriptionKey: 'catalog.descriptions.lovable',
  },
  // Data
  {
    name: 'Supabase',
    url: 'https://mcp.supabase.com/mcp',
    icon: 'logo:supabase.com:S',
    category: 'Data',
    description: 'Manage project and database',
  },
  {
    name: 'PostHog',
    url: 'https://mcp.posthog.com/mcp',
    icon: 'logo:posthog.com:P',
    category: 'Data',
    description: 'Query analytics and flags',
  },
  {
    name: 'Mixpanel',
    url: 'https://mcp.mixpanel.com/mcp',
    icon: 'logo:mixpanel.com:M',
    category: 'Data',
    description: 'Query events and funnels',
  },
  {
    name: 'Amplitude',
    url: 'https://mcp.amplitude.com/mcp',
    icon: 'logo:amplitude.com:A',
    category: 'Data',
    description: 'Query product analytics',
  },
  {
    name: 'Hugging Face',
    url: 'https://huggingface.co/mcp',
    icon: 'logo:huggingface.co:H',
    category: 'Data',
    description: 'Search AI models and datasets',
  },
  {
    name: 'Neon',
    url: 'https://mcp.neon.tech/mcp',
    icon: 'logo:neon.tech:N',
    category: 'Data',
    description: 'Manage Postgres databases',
  },
  {
    name: 'ClickHouse',
    url: 'https://mcp.clickhouse.cloud/mcp',
    icon: 'logo:clickhouse.com:C',
    category: 'Data',
    description: 'Run SQL and inspect schemas',
  },
  {
    name: 'PlanetScale',
    url: 'https://mcp.pscale.dev/mcp/planetscale',
    icon: 'logo:planetscale.com:P',
    category: 'Data',
    description: 'Query databases and branches',
  },
  {
    name: 'Prisma',
    url: 'https://mcp.prisma.io/mcp',
    icon: 'logo:prisma.io:P',
    category: 'Data',
    description: 'Provision and manage Postgres',
  },
  {
    name: 'MotherDuck',
    url: 'https://api.motherduck.com/mcp',
    icon: 'logo:motherduck.com:M',
    category: 'Data',
    description: 'Run SQL on MotherDuck',
  },
  {
    name: 'CockroachDB',
    url: 'https://cockroachlabs.cloud/mcp',
    icon: 'logo:cockroachlabs.com:C',
    category: 'Data',
    description: 'Query and manage clusters',
  },
  {
    name: 'Convex',
    url: 'https://mcp.convex.dev/mcp',
    icon: 'logo:convex.dev:C',
    category: 'Data',
    description: 'Query and manage Convex apps',
  },
  {
    name: 'Airbyte',
    url: 'https://mcp.airbyte.ai/mcp',
    icon: 'logo:airbyte.com:A',
    category: 'Data',
    description: 'Read and write across apps',
  },
  {
    name: 'CoinGecko',
    url: 'https://mcp.api.coingecko.com/mcp',
    icon: 'logo:coingecko.com:C',
    category: 'Data',
    description: 'Crypto prices and market data',
  },
  {
    name: 'Financial Datasets',
    url: 'https://mcp.financialdatasets.ai/',
    icon: 'logo:financialdatasets.ai:F',
    category: 'Data',
    description: 'Stock financials and filings',
  },
  {
    name: 'DeFiLlama',
    url: 'https://mcp.defillama.com/mcp',
    icon: 'logo:defillama.com:D',
    category: 'Data',
    description: 'DeFi TVL, prices, yields',
  },
  {
    name: 'Bitquery',
    url: 'https://mcp.bitquery.io',
    icon: 'logo:bitquery.io:B',
    category: 'Data',
    description: 'Query blockchain and DeFi data',
  },
  {
    name: 'Crustdata',
    url: 'https://mcp.crustdata.com/mcp',
    icon: 'logo:crustdata.com:C',
    category: 'Data',
    description: 'Look up company and people data',
  },
  {
    name: 'Digits',
    url: 'https://api.digits.com/mcp',
    icon: 'logo:digits.com:D',
    category: 'Data',
    description: 'Review accounting and finances',
  },
  {
    name: 'Dune',
    url: 'https://api.dune.com',
    icon: 'logo:dune.com:D',
    category: 'Data',
    description: 'Query onchain crypto analytics',
  },
  {
    name: 'Hex',
    url: 'https://app.hex.tech/mcp',
    icon: 'logo:hex.tech:H',
    category: 'Data',
    description: 'Run data notebooks and queries',
  },
  {
    name: 'Lightdash',
    url: 'https://app.lightdash.cloud/api/v1/mcp',
    icon: 'logo:lightdash.com:L',
    category: 'Data',
    description: 'Explore BI metrics and dashboards',
  },
  {
    name: 'Messari',
    url: 'https://mcp.messari.io',
    icon: 'logo:messari.io:M',
    category: 'Data',
    description: 'Research crypto markets and assets',
  },
  {
    name: 'Rillet',
    url: 'https://api.rillet.com',
    icon: 'logo:rillet.com:R',
    category: 'Data',
    description: 'Query accounting and ledgers',
  },
  {
    name: 'Vectorize',
    url: 'https://api.hindsight.vectorize.io/mcp',
    icon: 'logo:vectorize.io:V',
    category: 'Data',
    description: 'Retrieve from vector knowledge base',
  },
  {
    name: 'Consensus',
    url: 'https://mcp.consensus.app',
    icon: 'logo:consensus.app:C',
    category: 'Search',
    description: 'Search scientific research papers',
  },
  {
    name: 'Scite',
    url: 'https://api.scite.ai/mcp',
    icon: 'logo:scite.ai:S',
    category: 'Search',
    description: 'Search research literature',
  },
  // Search
  {
    name: 'Exa',
    url: 'https://mcp.exa.ai/mcp',
    icon: 'logo:exa.ai:E',
    category: 'Search',
    description: 'Web search and crawling',
  },
  {
    name: 'Tavily',
    url: 'https://mcp.tavily.com/mcp/',
    icon: 'logo:tavily.com:T',
    category: 'Search',
    description: 'Search, extract, crawl web',
  },
  {
    name: 'Apify',
    url: 'https://mcp.apify.com',
    icon: 'logo:apify.com:A',
    category: 'Search',
    description: 'Run scrapers to extract data',
  },
  {
    name: 'Ahrefs',
    url: 'https://api.ahrefs.com/mcp/mcp',
    icon: 'logo:ahrefs.com:A',
    category: 'Search',
    description: 'Get keyword and backlink data',
  },
  {
    name: 'Mapbox',
    url: 'https://mcp.mapbox.com',
    icon: 'logo:mapbox.com:M',
    category: 'Search',
    description: 'Search maps and geocode places',
  },
  {
    name: 'Jina AI',
    url: 'https://mcp.jina.ai/v1',
    icon: 'logo:jina.ai:J',
    category: 'Search',
    description: 'Read URLs and search the web',
  },
  {
    name: 'Context7',
    url: 'https://mcp.context7.com/mcp',
    icon: 'logo:context7.com:C',
    category: 'Search',
    description: 'Fetch up-to-date library docs',
  },
  {
    name: 'DeepWiki',
    url: 'https://mcp.deepwiki.com/mcp',
    icon: 'logo:deepwiki.com:D',
    category: 'Search',
    description: 'Ask questions about GitHub repos',
  },
  {
    name: 'Bright Data',
    url: 'https://mcp.brightdata.com/mcp',
    icon: 'logo:brightdata.com:B',
    category: 'Search',
    description: 'Scrape and search live web',
  },
  {
    name: 'Parallel',
    url: 'https://search.parallel.ai/mcp',
    icon: 'logo:parallel.ai:P',
    category: 'Search',
    description: 'AI-ready web search',
  },
  {
    name: 'You.com',
    url: 'https://api.you.com/mcp?profile=free',
    icon: 'logo:you.com:Y',
    category: 'Search',
    description: 'Search web and news',
  },
  {
    name: 'Ref',
    url: 'https://api.ref.tools/mcp',
    icon: 'logo:ref.tools:R',
    category: 'Search',
    description: 'Search developer documentation',
  },
  // Design
  {
    name: 'Canva',
    url: 'https://mcp.canva.com/mcp',
    icon: 'logo:canva.com:C',
    category: 'Design',
    description: 'Generate, edit, and export designs',
  },
  {
    name: 'Webflow',
    url: 'https://mcp.webflow.com/mcp',
    icon: 'logo:webflow.com:W',
    category: 'Design',
    description: 'Manage sites, CMS, and pages',
  },
  {
    name: 'Vimeo',
    url: 'https://mcp.vimeo.com/mcp',
    icon: 'logo:vimeo.com:V',
    category: 'Design',
    description: 'Search, manage, upload videos',
  },
  {
    name: 'Descript',
    url: 'https://api.descript.com/v2/mcp',
    icon: 'logo:descript.com:D',
    category: 'Design',
    description: 'Edit video, audio, transcripts',
  },
  {
    name: 'HeyGen',
    url: 'https://mcp.heygen.com/mcp/v1/',
    icon: 'logo:heygen.com:H',
    category: 'Design',
    description: 'Generate AI avatar videos',
  },
  {
    name: 'Runway',
    url: 'https://mcp.runwayml.com/mcp',
    icon: 'logo:runwayml.com:R',
    category: 'Design',
    description: 'Generate AI video and images',
  },
  {
    name: 'Cloudinary',
    url: 'https://asset-management.mcp.cloudinary.com/mcp',
    icon: 'logo:cloudinary.com:C',
    category: 'Developer',
    description: 'Upload and transform media',
  },
  {
    name: 'Ideogram',
    url: 'https://mcp.ideogram.ai/mcp',
    icon: 'logo:ideogram.ai:I',
    category: 'Design',
    description: 'Generate and edit images',
  },
  {
    name: 'Krea',
    url: 'https://api.krea.ai/mcp',
    icon: 'logo:krea.ai:K',
    category: 'Design',
    description: 'Generate AI images and video',
  },
  {
    name: 'OpenRouter',
    url: 'https://mcp.openrouter.ai/mcp',
    icon: 'logo:openrouter.ai:O',
    category: 'Developer',
    description: 'Route prompts across LLM models',
  },
  {
    name: 'Photoroom',
    url: 'https://mcp.photoroom.com/mcp',
    icon: 'logo:photoroom.com:P',
    category: 'Design',
    description: 'Remove backgrounds and edit photos',
  },
  {
    name: 'Pika',
    url: 'https://experiment-mcp.pika.art/api/mcp',
    icon: 'logo:pika.art:P',
    category: 'Design',
    description: 'Generate AI video clips',
  },
  {
    name: 'Icons8',
    url: 'https://mcp.icons8.com/mcp/',
    icon: 'logo:icons8.com:I',
    category: 'Design',
    description: 'Find icons and illustrations',
  },
  {
    name: 'Recraft',
    url: 'https://mcp.recraft.ai/mcp',
    icon: 'logo:recraft.ai:R',
    category: 'Design',
    description: 'Generate and vectorize images',
  },
  {
    name: 'LottieFiles',
    url: 'https://mcp.lottiefiles.com/mcp',
    icon: 'logo:lottiefiles.com:L',
    category: 'Design',
    description: 'Search Lottie animations',
  },
  {
    name: 'Black Forest Labs',
    url: 'https://mcp.bfl.ai/',
    icon: 'logo:bfl.ai:B',
    category: 'Design',
    description: 'Generate images with FLUX',
  },
  {
    name: 'Higgsfield',
    url: 'https://mcp.higgsfield.ai/mcp',
    icon: 'logo:higgsfield.ai:H',
    category: 'Design',
    description: 'Generate AI video clips',
  },
  {
    name: 'Shotstack',
    url: 'https://mcp.shotstack.io/',
    icon: 'logo:shotstack.io:S',
    category: 'Developer',
    description: 'Edit and render video',
  },
  {
    name: 'Magnific',
    url: 'https://mcp.magnific.com/mcp',
    icon: 'logo:magnific.com:M',
    category: 'Design',
    description: 'Upscale and enhance images',
  },
  // Travel
  {
    name: 'Kiwi.com',
    url: 'https://mcp.kiwi.com',
    icon: 'logo:kiwi.com:K',
    category: 'Travel',
    description: 'Search and book flights',
  },
  {
    name: 'Trivago',
    url: 'https://mcp.trivago.com/mcp',
    icon: 'logo:trivago.com:T',
    category: 'Travel',
    description: 'Search hotel deals',
  },
  {
    name: 'Viator',
    url: 'https://exp-app-mcp.prod.ep.viator.com/mcp',
    icon: 'logo:viator.com:V',
    category: 'Travel',
    description: 'Find tours and experiences',
  },
  {
    name: 'Skiplagged',
    url: 'https://mcp.skiplagged.com/mcp',
    icon: 'logo:skiplagged.com:S',
    category: 'Travel',
    description: 'Search cheap flights',
  },
  {
    name: 'Apaleo',
    url: 'https://mcp.apaleo.com/mcp/',
    icon: 'logo:apaleo.com:A',
    category: 'Travel',
    description: 'Manage hotel operations',
  },
  {
    name: 'AllTrails',
    url: 'https://www.alltrails.com/mcp',
    icon: 'logo:alltrails.com:A',
    category: 'Travel',
    description: 'Find hiking trails',
  },
];

// Advertisable size of the catalog, floored to the nearest ten. Derived rather than written down
// so onboarding copy can never overstate what we carry, and never quietly goes stale as servers
// are added — the floor also keeps the number from jittering on every single addition.
export const MCP_CATALOG_APPROX_COUNT = Math.floor(MCP_CATALOG.length / 10) * 10;

// The Popular spotlight: the best-known customer- and business-facing servers (weighted toward
// tools a support/sales agent actually uses over developer/infra tooling), shown first in browse
// mode. They also appear in their own category below (fine when browsing); search filters the flat
// MCP_CATALOG, so a match is still listed only once.
export const MCP_CATALOG_POPULAR_NAMES: string[] = [
  'Notion',
  'Stripe',
  'Calendly',
  'Canva',
  'Airtable',
  'PayPal',
  'monday',
  'Klaviyo',
  'Wix',
  'Pipedrive',
  'Gorgias',
  'AfterShip',
];

// Display-only brand name for a catalog server, since a provider with no `resource_name` derives a
// bare host server-side; derivation cannot replace it, as dropping the TLD renders Cal.com as "Cal".
const normalizeCatalogUrl = (url: string): string => url.trim().toLowerCase().replace(/\/+$/, '');

const CATALOG_BY_URL = new Map(
  MCP_CATALOG.map((entry) => [normalizeCatalogUrl(entry.url), entry] as const),
);

export const catalogDisplayNameForUrl = (url: string | undefined | null): string | undefined =>
  url ? CATALOG_BY_URL.get(normalizeCatalogUrl(url))?.name : undefined;

const CATALOG_BY_NAME = new Map(MCP_CATALOG.map((entry) => [entry.name, entry]));

export const catalogEntryByName = (name: string): McpCatalogEntry | undefined =>
  CATALOG_BY_NAME.get(name);

export const MCP_CATALOG_POPULAR: McpCatalogEntry[] = MCP_CATALOG_POPULAR_NAMES.map((name) =>
  CATALOG_BY_NAME.get(name),
).filter((entry): entry is McpCatalogEntry => Boolean(entry));
